'use client'

import { useQuery } from '@apollo/client'
import { Button, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useWorkspaceSetup } from '../../contexts/setup-workspace-context'
import { GET_COMPANY_BY_DOT_OR_MC, GET_WORKSPACE_BY_DOT_OR_MC } from '../../data/queries/workspace'
import { useSetupWorkspaceModal } from '../../stores/setup-workspace-modal-store'
import type { ICompany } from '../../types/company'
import type { IWorkspaceByDOTOrMCNumberReturnType } from '../../types/workspace'
import { SetupWorkspaceLayout } from './setup-workspace-layout'

export const dotOrMcNumberFormSchema = z.object({
  dotOrMCNumber: z.string().min(1, 'Please enter your DOT or MC number')
})

type DOTOrMCNumberFormValues = z.infer<typeof dotOrMcNumberFormSchema>

/**
 * This component is used to enter the DOT or MC number of the company.
 */
export const MCOrDOTNumber = () => {
  const { handleSetWorkspaceID } = useSetupWorkspaceModal()
  const { companyIdentifierType, companyIdentifierValue, setCompanyDetails, setCompanyIdentifierValue, setStep } =
    useWorkspaceSetup()
  const [gettingWorkspaceDetails, setGettingWorkspaceDetails] = useState<boolean>(false)
  const [showErrorLabel, setShowErrorLabel] = useState<boolean>(false)

  // Form setup
  const form = useForm({
    defaultValues: {
      dotOrMCNumber: ''
    },
    resolver: zodResolver(dotOrMcNumberFormSchema)
  })

  //To-do: Replace this with useLazyQuery
  const { refetch: refetchWorkspace } = useQuery<{
    getWorkspaceByDOTOrMCNumber: IWorkspaceByDOTOrMCNumberReturnType | null
  }>(GET_WORKSPACE_BY_DOT_OR_MC, {
    fetchPolicy: 'network-only',
    skip: true
  })

  //To-do: Replace this with useLazyQuery
  const { refetch: refetchCompany } = useQuery<{
    company: ICompany | null
  }>(GET_COMPANY_BY_DOT_OR_MC, {
    fetchPolicy: 'network-only',
    skip: true
  })

  // This function is used to submit the DOT or MC number of the company.
  const onSubmit = async (values: DOTOrMCNumberFormValues) => {
    try {
      // set the DOT or MC number of the company
      setCompanyIdentifierValue(values.dotOrMCNumber)
      setGettingWorkspaceDetails(true)
      // refetch the workspace data
      const workspaceData = await refetchWorkspace({ dotOrMCNumber: values.dotOrMCNumber, type: companyIdentifierType })
      if (workspaceData.data?.getWorkspaceByDOTOrMCNumber) {
        // set the workspace ID
        handleSetWorkspaceID(workspaceData.data?.getWorkspaceByDOTOrMCNumber.id)
      }
      // refetch the company data
      const companyData = await refetchCompany({ id: values.dotOrMCNumber, type: companyIdentifierType })
      if (companyData.data?.company) {
        // set the company details
        setCompanyDetails(companyData.data?.company)
        setStep(4) // Setting the step to 4 will trigger the next step in the setup process which is the CompanyConfirmation component.
      } else {
        // set the show error label to true
        setShowErrorLabel(true)
      }
      setGettingWorkspaceDetails(false)
    } catch (error) {
      setGettingWorkspaceDetails(false)
    }
  }

  return (
    <SetupWorkspaceLayout
      title={`Enter your ${companyIdentifierType}`}
      footer={{
        label: 'Back',
        onClick: () => setStep(2) // Setting the step to 2 will trigger the previous step in the setup process which is the SelectMCOrDOTType component.
      }}
    >
      <div className="bg-transparent sm:bg-card rounded-xl w-full" data-testid="login-form-container">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="login-form">
            <FormField
              control={form.control}
              name="dotOrMCNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{companyIdentifierType}#</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      onChange={(e) => {
                        field.onChange(e)
                      }}
                      onKeyDown={() => setShowErrorLabel(false)}
                      disabled={gettingWorkspaceDetails}
                      placeholder={`Enter the ${companyIdentifierType}#`}
                      className="w-full"
                      aria-label={`${companyIdentifierType}# input`}
                      initialFocus={true}
                    />
                  </FormControl>
                  <FormMessage />
                  {!!showErrorLabel && <FormMessage>Company not found with {companyIdentifierValue}</FormMessage>}
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              variant="default"
              disabled={gettingWorkspaceDetails}
              loading={gettingWorkspaceDetails}
              data-testid="submit-button"
            >
              Next
            </Button>
          </form>
        </Form>
      </div>
    </SetupWorkspaceLayout>
  )
}
